import React, { createContext, useContext, useReducer, ReactNode, useCallback, useEffect, useRef } from 'react';
import { TenderState, TenderAction, TenderContextType, SpoilReconciliationData } from '../types/tender.types';
import { saveTenderToStorage, loadSpecificProject, loadTenderFromStorage } from '../utils/storage';
import { setOpportunityNo } from '../api/client';
import { calculatePlotValues } from '../utils/plotCalculations';
import { updateCurrentBoqVersionFromState } from '../utils/boqVersionUtils';
import { calculateAllTenderTotals } from '../utils/summaryCalculations';

const initialState: TenderState = {
  id: '',
  projectName: '',
  client: '',
  siteAddress: '',
  region: 'West Midlands',
  status: 'In-progress',
  boqStatus: 'In-progress',
  isViewMode: false,
  jobTypes: {
    roadsAndSewers: false,
    groundworks: false,
  },
  activeJobType: 'roadsAndSewers',
  workflow: {
    roadsAndSewers: {
      currentStep: 0,
      isComplete: false,
    },
    groundworks: {
      currentStep: 0,
      isComplete: false,
    },
  },
  globalSettings: {
    pipeType: 'clay',
    backfillType: 'type1',
    allowOverrides: false,
    roadsAndSewers: {
      overheadAndProfit: 0,
      preliminaries: 0,
      discount: 0,
    },
    groundworks: {
      overheadAndProfit: 0,
      preliminaries: 0,
      discount: 0,
    },
  },
  foulSewers: {
    manholes: { table: [], config: {} },
    pipeRuns: { table: [], config: {} },
    lateralConnections: { table: [], config: { lateralsCount: 0, gulliesCount: 0 } },
  },
  pumpStation: { table: {}, config: {} },
  risingMain: { table: {}, config: {} },
  stormSewers: {
    manholes: { table: [], config: {} },
    pipeRuns: { table: [], config: { roadGulliesCount: 0 } },
    lateralConnections: { table: [], config: {} },
    gullyConnections: { table: [], config: { gullyConnectionLength: 6 } },
  },
  boxCulverts: { table: {}, config: {} },
  cellularAttenuation: { table: {}, config: {} },
  concAttenuation: { table: {}, config: {} },
  pondsSwales: { table: {}, config: {} },
  weholiteAttenuation: { table: {}, config: {} },
  roads: {
    roadTypes: { table: [], config: {} },
    cutFillSections: { table: [], config: { stripValueDefault: 0 } },
    carriagewayFootways: { table: [], config: { totalBuildUpValue: 0 } },
    capping: {
      table: [],
      config: {
        cappingLayer: {
          enabled: false,
          thicknesses: [],
        },
      },
    },
    footpaths: { table: [], config: {} },
  },
  mainServices: { table: {}, config: {} },
  substation: { table: {}, config: {} },
  preliminaryItems: {
    autoCalculated: [],
    manualItems: [],
    mainServices: [],
    pumpStation: null,
    totals: {
      autoCalculatedTotal: 0,
      manualItemsTotal: 0,
      mainServicesTotal: 0,
      pumpStationTotal: 0,
      grandTotal: 0,
    },
  },
  groundworks: {
    plots: [],
    foundations: [],
    // Initialize all table properties to empty arrays
    cutFillsTable: [],
    foundationsTable: [],
    gardenTable: [],
    plotPavingsTable: [],
    scaffoldTable: [],
    plotServiceTable: [],
    retainingWallsTable: [],
    wallsBuildUpData: {},
    plotDrivesTable: [],
    drainageTable: [],
    geoToberWallTable: [],
    cutFills: {
      paths: [],
      gardens: [],
      privateDrives: [],
      sharedDrives: [],
      drainageComponents: [],
      settings: {
        privateTarmacValue: 0,
        privateBlockValue: 0,
        sharedTarmacValue: 0,
        sharedBlockValue: 0,
      },
    },
    externalWorks: {
      settings: {
        gardenMeasurementMethod: 'individual',
        defaultTopsoilDepth: 150,
        standardDriveSpec: 'standard_block_paving',
        topsoilStripDepth: 300,
        topsoilImportRate: 35.00,
        topsoilExportRate: 12.00,
        privateTarmacValue: 0,
        privateBlockValue: 0,
        sharedTarmacValue: 0,
        sharedBlockValue: 0
      },
      specifications: [
        {
          id: 'standard_block_paving',
          name: 'Standard Block Paving',
          surfaceType: 'block_paving',
          surfaceThickness: 60,
          bedding: { material: 'sharp_sand', thickness: 50 },
          subBase: { material: 'type1', thickness: 200 },
          totalDepth: 310,
          isStandard: true
        },
        {
          id: 'standard_tarmac',
          name: 'Standard Tarmac',
          surfaceType: 'tarmac',
          surfaceThickness: 40,
          binderCourse: { thickness: 60 },
          subBase: { material: 'type1', thickness: 200 },
          totalDepth: 300,
          isStandard: true
        }
      ],
      privateDrives: [],
      sharedDrives: [],
      paths: [],
      gardens: [],
      topsoilBalance: {
        totalStrippedVolume: 0,
        totalRequiredVolume: 0,
        balance: 0,
        importRequired: false,
        exportRequired: false,
        importCost: 0,
        exportCost: 0
      },
      totals: {
        privateDrivesTotal: 0,
        sharedDrivesTotal: 0,
        pathsTotal: 0,
        gardensTotal: 0,
        topsoilAdjustmentTotal: 0,
        grandTotal: 0
      }
    },
    privateDrainage: {
      settings: {
        measurementMethod: 'sampling_20_percent',
        region: 'southern',
        samplingPercentage: 20,
        contingencyPercentage: 10,
        floorAreaFactor: {
          '100mm': 0.10,
          '150mm': 0.15,
          '225mm': 0.05
        },
        fittingFactors: {
          bendsPerMeter: 1/6,
          junctionsPerMeter: 1/10,
          collarsPerMeter: 1/6,
          restBendsPerMeter: 1/12
        },
        rates: {
          excavation: {
            shallow: 18.50,
            deep: 28.75,
            rock: 45.00
          },
          pipes: {
            '100mm': 8.50,
            '150mm': 12.75,
            '225mm': 18.50
          },
          fittings: {
            bends: { '100mm': 3.50, '150mm': 5.25, '225mm': 8.00 },
            junctions: { '100mm': 8.50, '150mm': 12.75, '225mm': 18.50 },
            collars: { '100mm': 2.25, '150mm': 3.50, '225mm': 5.25 },
            restBends: { '100mm': 4.75, '150mm': 7.25, '225mm': 11.50 }
          },
          chambers: {
            plastic_shallow: 125.00,
            plastic_deep: 185.00,
            concrete_shallow: 185.00,
            concrete_deep: 285.00
          },
          connections: {
            stack_connection: 45.00,
            main_sewer_connection: 125.00
          },
          bedding: 35.00,
          backfill: 12.50,
          compaction: 8.75
        }
      },
      sampledPlots: [],
      summary: {
        totalPlots: 0,
        sampledPlots: 0,
        pipeQuantities: {},
        fittingQuantities: {
          bends: {},
          junctions: {},
          collars: {},
          restBends: {}
        },
        chamberQuantities: {},
        connectionQuantities: {},
        pipeCosts: {},
        fittingCosts: 0,
        chamberCosts: 0,
        connectionCosts: 0,
        excavationCosts: 0,
        totalCost: 0
      },
      plotsNotMeasured: [],
      unusualValues: [],
      totals: {
        totalCost: 0,
        contingencyAmount: 0,
        grandTotal: 0
      }
    },
    additionalItems: {
      retainingWalls: [],
      attenuationCrates: [],
      compoundAreas: [],
      customCalculations: [],
      totals: {
        retainingWallsTotal: 0,
        attenuationCratesTotal: 0,
        compoundAreasTotal: 0,
        customCalculationsTotal: 0,
        grandTotal: 0
      }
    },
    matrix: {
      settings: {
        projectType: 'residential',
        stages: [],
        defaultStages: {
          siteClearance: { id: 'site_clearance', name: 'Site Clearance', standardPercentage: 8, isActive: true, order: 1 },
          foundationExcavation: { id: 'foundation_excavation', name: 'Foundation Excavation', standardPercentage: 18, isActive: true, order: 2 },
          foundationConcrete: { id: 'foundation_concrete', name: 'Foundation Concrete', standardPercentage: 20, isActive: true, order: 3 },
          foundationBlockwork: { id: 'foundation_blockwork', name: 'Foundation Blockwork', standardPercentage: 12, isActive: true, order: 4 },
          foundationFloor: { id: 'foundation_floor', name: 'Foundation Floor', standardPercentage: 18, isActive: true, order: 5 },
          drainage: { id: 'drainage', name: 'Drainage', standardPercentage: 12, isActive: true, order: 6 },
          externalWorksDrives: { id: 'external_works_drives', name: 'External Works - Drives', standardPercentage: 8, isActive: true, order: 7 },
          externalWorksGardens: { id: 'external_works_gardens', name: 'External Works - Gardens', standardPercentage: 4, isActive: true, order: 8 }
        },
        cashFlowSettings: {
          enabled: false,
          monthlyBreakdown: false,
          programme: {
            startDate: new Date(),
            plotsPerMonth: 4,
            stageDelays: {}
          }
        }
      },
      plotData: [],
      stageTotals: {},
      projectTotals: {
        totalValue: 0,
        totalAdjustments: 0,
        adjustedTotal: 0
      },
      exportOptions: {
        includeBreakdown: true,
        includeAdjustments: true,
        clientFriendly: false,
        format: 'excel'
      }
    },
    totals: {
      foundationsTotal: 0,
      externalWorksTotal: 0,
      privateDrainageTotal: 0,
      additionalItemsTotal: 0,
      grandTotal: 0,
    },
    // Initialize additional groundworks properties
    plotMatrix: [],
    summarySheet: undefined,
    externalsSection: undefined,
    plotExternals: {},
  },
  spoilReconciliation: {
    config: {
    topsoilRoadFootpathsStrip: 0,
    topsoilPondsStrip: 0,
    topsoilPOSStrip: 0,
    topsoilAttenuationTanksStrip: 0,
    topsoilCulvertsStrip: 0,
    topsoilRoadFootpathsReuse: 0,
    topsoilPondsReuse: 0,
    topsoilPOSReuse: 0,
    topsoilAttenuationTanksReuse: 0,
    topsoilCulvertsReuse: 0,
    subsoilRoadCut: 0,
    subsoilPathsCut: 0,
    subsoilFoulSewersCut: 0,
    subsoilStormSewersCut: 0,
    subsoilPondsCut: 0,
    subsoilPOSCut: 0,
    subsoilAttenuationTanksCut: 0,
    subsoilCulvertsCut: 0,
    subsoilMainsServicesCut: 0,
    subsoilRoadFill: 0,
    subsoilPathsFill: 0,
    subsoilPondsFill: 0,
    subsoilPOSFill: 0,
    subsoilAttenuationTanksFill: 0,
    subsoilCulvertsFill: 0,
    subsoilMainsServicesFill: 0,
    } as SpoilReconciliationData,
  },
  calculationResults: null,
  dateCreated: new Date(),
  lastModified: new Date(),
  currentStep: 0,
  isValid: false,
  isDirty: false,
};

const tenderReducer = (state: TenderState, action: TenderAction): TenderState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
        isDirty: true,
        lastModified: new Date(),
      };
    // Shallow-merges into `groundworks` off the reducer's OWN (always-current)
    // `state.groundworks`, instead of a value pre-computed in a component
    // effect's closure (`updateField('groundworks', { ...state.groundworks, x })`).
    // Two sibling hydration effects (e.g. dynamicBillSummaryTabs and
    // cutFillsExtraItems in GroundworksReview.tsx, both landing in the same
    // React 18 batch on page-load) each compute their spread from the same
    // stale `state.groundworks` closure snapshot — whichever UPDATE_FIELD
    // dispatches second overwrites the field the first one just added, since
    // UPDATE_FIELD fully replaces `groundworks`. Routing through the reducer's
    // live state instead means every MERGE_GROUNDWORKS_FIELDS action, however
    // many land in the same batch, correctly accumulates onto one another.
    case 'MERGE_GROUNDWORKS_FIELDS':
      return {
        ...state,
        groundworks: {
          ...state.groundworks,
          ...action.payload,
        },
        isDirty: true,
        lastModified: new Date(),
      };
    case 'SET_STEP':
      return {
        ...state,
        currentStep: action.payload,
      };
    case 'SET_DIRTY':
      return {
        ...state,
        isDirty: action.payload,
      };
    case 'SET_VALID':
      return {
        ...state,
        isValid: action.payload,
      };
    case 'SAVE_TENDER':
      return {
        ...state,
        isDirty: false,
        lastModified: new Date(),
      };
    case 'RESET_TENDER':
      return {
        ...initialState,
        id: Date.now().toString(),
      };
    case 'LOAD_PROJECT':
      return {
        ...action.payload,
        isDirty: false,
        lastModified: new Date(),
      };
    case 'SET_JOB_TYPES':
      return {
        ...state,
        jobTypes: {
          roadsAndSewers: action.payload.roadsAndSewers,
          groundworks: action.payload.groundworks,
        },
        isDirty: true,
        lastModified: new Date(),
      };
    case 'SET_ACTIVE_JOB_TYPE':
      return {
        ...state,
        activeJobType: action.payload,
        lastModified: new Date(),
      };
    case 'SET_WORKFLOW_STEP':
      return {
        ...state,
        workflow: {
          ...state.workflow,
          [action.payload.jobType]: {
            ...state.workflow[action.payload.jobType as keyof typeof state.workflow],
            currentStep: action.payload.step,
          },
        },
        lastModified: new Date(),
      };
    case 'SET_VIEW_MODE':
      return {
        ...state,
        isViewMode: action.payload,
      };
    default:
      return state;
  }
};

const TenderContext = createContext<TenderContextType | undefined>(undefined);

export const TenderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Load saved data from localStorage or use initial state (lazy initialization)
  const loadInitialState = (): TenderState => {
    const savedData = loadTenderFromStorage();

    // Check if we're in view mode from sessionStorage
    const isViewModeFromSession = sessionStorage.getItem('isViewMode') === 'true';

    if (savedData) {
      return {
        ...savedData,
        isViewMode: isViewModeFromSession, // Restore view mode from session
      };
    }
    return {
      ...initialState,
      id: Date.now().toString(),
      dateCreated: new Date(),
      isViewMode: isViewModeFromSession, // Set view mode for new state
    };
  };

  // Ref to always have access to the latest state (avoids stale closure issues)
  const stateRef = useRef<TenderState>(initialState);

  // Custom reducer wrapper that updates stateRef synchronously (not in useEffect)
  // This ensures stateRef.current is always up-to-date immediately after dispatch
  const [state, dispatch] = useReducer((currentState: TenderState, action: TenderAction) => {
    const newState = tenderReducer(currentState, action);
    stateRef.current = newState; // Update ref synchronously
    return newState;
  }, undefined, () => {
    const initialLoadedState = loadInitialState();
    stateRef.current = initialLoadedState; // Initialize ref with loaded state
    return initialLoadedState;
  });

  // Ref to store before-save callbacks (keyed by component name)
  const beforeSaveCallbacksRef = useRef<Map<string, () => Promise<void> | void>>(new Map());

  // Listen for version data reloads and update state
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent | Event) => {
      // Handle both StorageEvent (from other tabs) and custom Event (from same tab)
      const storageEvent = e as StorageEvent;

      // Check if currentTender or version data was updated
      if (!storageEvent.key || storageEvent.key === 'currentTender' || storageEvent.key === 'currentBoqVersion') {
        const savedData = loadTenderFromStorage();
        if (savedData) {
          // Preserve view mode state when reloading
          const isViewModeFromSession = sessionStorage.getItem('isViewMode') === 'true';
          dispatch({
            type: 'LOAD_PROJECT',
            payload: {
              ...savedData,
              isViewMode: isViewModeFromSession
            }
          });
        }
      }
    };

    // Listen for storage events from other tabs and custom events from same tab
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('versionDataReloaded', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('versionDataReloaded', handleStorageChange);
    };
  }, []);

  // Keep the Axios default header in sync so the request interceptor can inject
  // oppNo into every /boq-versions request automatically.
  useEffect(() => {
    setOpportunityNo(state.opportunityNo);
  }, [state.opportunityNo]);

  const updateField = useCallback((field: keyof TenderState, value: any) => {
    dispatch({ type: 'UPDATE_FIELD', payload: { field, value } });
  }, []);

  // Helper function to update a specific groundworks table
  // This ensures all other tables are preserved when updating one table
  // Also triggers immediate save for Excel imports
  const updateGroundworksTable = useCallback((tableName: string, tableData: any[], triggerSave: boolean = true) => {
    const currentGroundworks = state.groundworks || {};
    const updatedGroundworks = {
      ...currentGroundworks,
      [tableName]: tableData,
    };
    dispatch({ type: 'UPDATE_FIELD', payload: { field: 'groundworks', value: updatedGroundworks } });

    // ============================================================
    // AUTO-SAVE DISABLED - Commented out for now
    // ============================================================
    // // Trigger immediate save after updating table (for imports)
    // if (triggerSave && typeof window !== 'undefined') {
    //   // Small delay to ensure dispatch completes
    //   setTimeout(() => {
    //     window.dispatchEvent(new CustomEvent('excel-import-complete', {
    //       detail: { recordCount: tableData.length, tableName }
    //     }));
    //   }, 50);
    // }
  }, [state.groundworks]);

  const setCurrentStep = useCallback((step: number) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  const setJobTypes = useCallback((roadsAndSewers: boolean, groundworks: boolean) => {
    dispatch({ type: 'SET_JOB_TYPES', payload: { roadsAndSewers, groundworks } });
  }, []);

  const setActiveJobType = useCallback((jobType: 'roadsAndSewers' | 'groundworks') => {
    dispatch({ type: 'SET_ACTIVE_JOB_TYPE', payload: jobType });
  }, []);

  const setWorkflowStep = useCallback((jobType: 'roadsAndSewers' | 'groundworks', step: number) => {
    dispatch({ type: 'SET_WORKFLOW_STEP', payload: { jobType, step } });
  }, []);

  const setViewMode = useCallback((isViewMode: boolean) => {
    dispatch({ type: 'SET_VIEW_MODE', payload: isViewMode });
    // Persist view mode state to sessionStorage
    if (isViewMode) {
      sessionStorage.setItem('isViewMode', 'true');
    } else {
      sessionStorage.removeItem('isViewMode');
    }
  }, []);

  const saveTender = useCallback(async () => {
    // Execute all registered before-save callbacks
    const callbacks = Array.from(beforeSaveCallbacksRef.current.values());
    for (const callback of callbacks) {
      try {
        await callback();
      } catch (error) {
        console.error('Error in before-save callback:', error);
      }
    }

    // Use stateRef.current to always get the latest state (avoids stale closure issues)
    let currentState = stateRef.current;

    // Use centralized calculation function for consistent totaling
    if (currentState.groundworks) {
      // Calculate base total first
      const totals = calculateAllTenderTotals(currentState);
      const baseTotal = totals.groundworksTotal;

      // Helper to read checkbox state from localStorage
      const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
        const saved = localStorage.getItem(key);
        if (saved !== null) {
          return JSON.parse(saved);
        }
        return fallbackPercent > 0;
      };

      // Read fresh settings from current state
      const preliminariesPercent = currentState.globalSettings?.groundworks?.preliminaries || 0;
      const ohpPercent = currentState.globalSettings?.groundworks?.overheadAndProfit || 0;
      const discountPercent = currentState.globalSettings?.groundworks?.discount || 0;

      // Read checkbox states from localStorage
      const includePreliminaries = getCheckboxState('groundworks_includePreliminariesDisplay', preliminariesPercent);
      const includeOhp = getCheckboxState('groundworks_includeOhpDisplay', ohpPercent);
      const includeDiscount = getCheckboxState('groundworks_includeDiscountDisplay', discountPercent);

      // Calculate adjusted total with prelims, OH&P, and discount
      // Both preliminaries and OH&P are calculated on the BASE amount
      let preliminariesAmount = 0;
      let ohpAmount = 0;
      let discountAmount = 0;

      // Step 1: Calculate preliminaries on base total
      if (includePreliminaries && preliminariesPercent > 0) {
        preliminariesAmount = baseTotal * (preliminariesPercent / 100);
      }

      // Step 2: Calculate OH&P on BASE total (not on total with preliminaries)
      if (includeOhp && ohpPercent > 0) {
        ohpAmount = baseTotal * (ohpPercent / 100);
      }

      // Calculate total before discount
      let groundworksTotal = baseTotal + preliminariesAmount + ohpAmount;

      // Step 3: Subtract discount from final total
      if (includeDiscount && discountPercent > 0) {
        discountAmount = groundworksTotal * (discountPercent / 100);
        groundworksTotal -= discountAmount;
      }

      // Update state with calculated totals
      currentState = {
        ...currentState,
        groundworks: {
          ...currentState.groundworks,
          totals: {
            ...currentState.groundworks?.totals,
            grandTotal: groundworksTotal,
          },
        },
      };

      // Update the state ref and dispatch to update React state
      stateRef.current = currentState;
      dispatch({
        type: 'UPDATE_FIELD',
        payload: {
          field: 'groundworks',
          value: currentState.groundworks,
        },
      });
    }

    saveTenderToStorage(currentState);
    dispatch({ type: 'SAVE_TENDER' });

    // ============================================================
    // AUTO-SAVE API UPDATE DISABLED - Commented out for now
    // ============================================================
    // // Skip BOQ API update if no job types selected (e.g., on TenderInfo page)
    // // This prevents duplicate API calls when manual submission handles the API call
    // const hasSelectedJobTypes = currentState.jobTypes?.roadsAndSewers || currentState.jobTypes?.groundworks;
    // if (!hasSelectedJobTypes) {
    //   return;
    // }

    // // Update BOQ version via API if version info exists
    // try {
    //   await updateCurrentBoqVersionFromState(currentState, 'Auto-saved changes');
    // } catch (error) {
    //   console.error('Error updating BOQ version:', error);
    //   // Don't throw - save to localStorage was successful, API update is supplementary
    // }
  }, []);

  const resetTender = useCallback(() => {
    localStorage.removeItem('mjEvans_tender_draft');
    dispatch({ type: 'RESET_TENDER' });
  }, []);

  const loadProject = useCallback((projectId: string): boolean => {
    const project = loadSpecificProject(projectId);
    if (project) {
      dispatch({ type: 'LOAD_PROJECT', payload: project });
      return true;
    }
    return false;
  }, []);

  // Automatically calculate plot values whenever groundworks plots change
  useEffect(() => {
    if (state.groundworks?.plots && state.groundworks.plots.length > 0) {
      const needsRecalculation = state.groundworks.plots.some(plot => {
        // Check if plot has required fields for calculation but missing calculated values
        return (plot.existingGroundLevel && plot.finishedFloorLevel) && 
               (plot.cutVolume === undefined || plot.fillVolume === undefined);
      });

      if (needsRecalculation) {
        const recalculatedPlots = state.groundworks.plots.map(plot => {
          const calculated = calculatePlotValues(plot);
          return { ...plot, ...calculated };
        });

        // Update the state with recalculated plots
        dispatch({ 
          type: 'UPDATE_FIELD', 
          payload: { 
            field: 'groundworks', 
            value: { 
              ...state.groundworks, 
              plots: recalculatedPlots 
            } 
          } 
        });
      }
    }
  }, [state.groundworks?.plots]);

  // Register a callback to be called before save
  const registerBeforeSave = useCallback((key: string, callback: () => Promise<void> | void) => {
    beforeSaveCallbacksRef.current.set(key, callback);
  }, []);

  // Unregister a before-save callback
  const unregisterBeforeSave = useCallback((key: string) => {
    beforeSaveCallbacksRef.current.delete(key);
  }, []);

  const value: TenderContextType = {
    state,
    dispatch,
    updateField,
    updateGroundworksTable,
    setCurrentStep,
    setJobTypes,
    setActiveJobType,
    setWorkflowStep,
    setViewMode,
    saveTender,
    resetTender,
    loadProject,
    registerBeforeSave,
    unregisterBeforeSave,
  };

  return <TenderContext.Provider value={value}>{children}</TenderContext.Provider>;
};

/** Open tender's site identity (project name, opportunity number(s), region), or all
 * `undefined` when there is no TenderProvider — so non-tender hooks like
 * `useItemLibrary` can read it reactively without throwing. */
export const useTenderSiteScope = (): { siteName?: string; oppNo?: string; region?: string } => {
  const state = useContext(TenderContext)?.state;
  return {
    siteName: state?.projectName || undefined,
    oppNo: state?.opportunityNo || undefined,
    region: state?.region || undefined,
  };
};

export const useTender = (): TenderContextType => {
  const context = useContext(TenderContext);
  if (context === undefined) {
    throw new Error('useTender must be used within a TenderProvider');
  }
  return context;
};